import {
  LayoutDashboard,
  Users,
  Car,
  Package,
  Tag,
  ShieldCheck,
  ShieldOff,
  FileText,
  Settings,
  CreditCard,
  Receipt,
  AlertTriangle,
  MessageCircle,
  MessageSquare,
  Send,
  MapPin,
  Cog,
  Activity,
  Droplets,
  Ban,
  Banknote,
  Building2,
  UserPlus2,
  PauseCircle,
  Share2,
  BarChart3,
  Wrench,
  AlertOctagon,
  Settings2,
  Webhook,
  Shield,
  Crown,
  Flag,
  Award,
  Ticket,
  Wallet,
  ToggleRight,
  Fuel,
  Store,
  Image as ImageIcon,
} from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { useAuth } from '@/lib/auth-context';
import { useTenant } from '@/lib/tenant-context';
import { cn } from '@/lib/utils';

interface NavItem {
  label: string;
  href: string;
  icon: typeof LayoutDashboard;
  soSuperAdmin?: boolean;
}

const NAV_GROUPS: { titulo?: string; items: NavItem[] }[] = [
  {
    items: [
      { label: 'Inicio', href: '/dashboard', icon: LayoutDashboard },
      { label: 'Clientes', href: '/clientes', icon: Users },
    ],
  },
  {
    titulo: 'Frota',
    items: [{ label: 'Veiculos', href: '/veiculos', icon: Car }],
  },
  {
    titulo: 'Catalogo',
    items: [
      { label: 'Planos', href: '/planos', icon: Package },
      { label: 'Promocoes', href: '/promocoes', icon: Tag },
      { label: 'Banners do carrossel', href: '/banners', icon: ImageIcon },
    ],
  },
  {
    titulo: 'Financeiro',
    items: [
      { label: 'Assinaturas', href: '/assinaturas', icon: CreditCard },
      { label: 'Pagamentos', href: '/pagamentos', icon: Receipt },
      { label: 'Inadimplências', href: '/inadimplencias', icon: AlertTriangle },
      { label: 'Chargebacks', href: '/chargebacks', icon: ShieldOff },
    ],
  },
  {
    titulo: 'Operacao',
    items: [
      { label: 'Operação ao vivo', href: '/operacao', icon: Activity },
      { label: 'Unidades', href: '/unidades', icon: MapPin },
      { label: 'Maquinas', href: '/maquinas', icon: Cog },
      { label: 'Histórico de lavagens', href: '/lavagens', icon: Droplets },
      { label: 'Bloqueios', href: '/bloqueios', icon: Ban },
    ],
  },
  {
    titulo: 'Comunicação',
    items: [
      { label: 'Instâncias WhatsApp', href: '/mensagens/uazapi', icon: MessageCircle },
      { label: 'Templates', href: '/mensagens/templates', icon: MessageSquare },
      { label: 'Mensagens enviadas', href: '/mensagens/enviadas', icon: Send },
    ],
  },
  {
    titulo: 'Avancado',
    items: [
      { label: 'Pagamentos avulsos', href: '/avulso', icon: Banknote },
      { label: 'Preços lavagem avulsa', href: '/precos-avulso', icon: Wallet },
      { label: 'Pausas de assinatura', href: '/pausas', icon: PauseCircle },
      { label: 'Indicacoes', href: '/indicacoes', icon: Share2 },
      { label: 'Modulos do tenant', href: '/modulos', icon: ToggleRight },
      { label: 'AutoSystem (Linx)', href: '/autosystem', icon: Fuel },
      { label: 'Fidelidade (admin)', href: '/fidelidade-admin', icon: Award },
      { label: 'Parceiros e cupons', href: '/parceiros', icon: Ticket },
      { label: 'Parceiros de resgate', href: '/parceiros-resgate', icon: Store },
      { label: 'Alertas operacionais', href: '/alertas', icon: AlertOctagon },
      { label: 'Saúde do sistema', href: '/saude', icon: Activity },
      { label: 'Eventos webhook', href: '/webhooks', icon: Webhook },
      { label: 'Feature flags', href: '/features', icon: Flag },
      { label: 'Metricas por período', href: '/metricas', icon: BarChart3 },
      { label: 'Avaliações (NPS)', href: '/avaliacoes', icon: MessageSquare },
      { label: 'Diagnostico tenant', href: '/diagnostico', icon: ShieldCheck },
      { label: 'Corporativo', href: '/corporativo', icon: Building2 },
      { label: 'Multi-login', href: '/multi-login', icon: UserPlus2 },
      { label: 'Painel operacional', href: '/painel-op', icon: BarChart3 },
      { label: 'Hardware', href: '/hardware', icon: Wrench },
      { label: 'Simular hardware', href: '/hardware/simular', icon: Settings2 },
    ],
  },
  {
    titulo: 'LGPD',
    items: [
      { label: 'Solicitacoes', href: '/lgpd/solicitacoes', icon: ShieldCheck },
      { label: 'Logs de Auditoria', href: '/lgpd/logs', icon: FileText },
    ],
  },
  {
    titulo: 'Configuracoes',
    items: [
      { label: 'Configuracoes', href: '/configuracoes', icon: Settings },
      { label: 'Anti-fraude (parametros)', href: '/anti-fraude', icon: AlertOctagon },
      { label: 'Painel anti-fraude', href: '/fraude', icon: AlertOctagon },
      { label: 'Autenticação 2FA', href: '/seguranca/2fa', icon: Shield },
      { label: 'Super Admin', href: '/super-admin', icon: Crown, soSuperAdmin: true },
    ],
  },
];

export function Sidebar() {
  const { tenant } = useTenant();
  const { role } = useAuth();

  return (
    <aside className="bg-sidebar text-sidebar-foreground hidden h-screen w-64 shrink-0 flex-col border-r lg:flex">
      <div className="border-sidebar-border flex h-16 items-center gap-3 border-b px-6">
        {tenant?.des_logo_url ? (
          <img
            src={tenant.des_logo_url}
            alt={tenant.nom_tenant}
            className="h-9 w-9 rounded-md object-cover"
          />
        ) : (
          <div className="bg-primary text-primary-foreground flex h-9 w-9 items-center justify-center rounded-md font-bold">
            {(tenant?.nom_tenant ?? 'OW').slice(0, 2).toUpperCase()}
          </div>
        )}
        <div className="flex flex-col">
          <span className="text-sm font-semibold leading-tight">
            {tenant?.nom_tenant ?? 'Octa Pontos'}
          </span>
          <span className="text-sidebar-foreground/60 text-[10px] uppercase tracking-wider">
            Painel
          </span>
        </div>
      </div>

      <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-4">
        {NAV_GROUPS.map((grupo, idx) => (
          <div key={idx}>
            {grupo.titulo && (
              <div className="text-sidebar-foreground/50 mb-2 px-3 text-[10px] font-semibold uppercase tracking-wider">
                {grupo.titulo}
              </div>
            )}
            <ul className="space-y-1">
              {grupo.items
                .filter((item) =>
                  'soSuperAdmin' in item && item.soSuperAdmin ? role === 'super_admin' : true,
                )
                .map((item) => (
                  <li key={item.href}>
                    <NavLink
                      to={item.href}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                          isActive
                            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                            : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground',
                        )
                      }
                    >
                      <item.icon className="h-4 w-4" />
                      {item.label}
                    </NavLink>
                  </li>
                ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-sidebar-border text-sidebar-foreground/40 border-t px-6 py-3 text-[10px]">
        v0.0.1 (Sprint 1)
      </div>
    </aside>
  );
}
